/**
 * Sales Advisor Service — Model-agnostic AI sales advisor for CRM
 * Uses any provider supported by aiService.ts (Gemini, OpenAI, Anthropic)
 */

import { callAI, AIProvider, AIConfig, AIResponse } from './aiService';
import { getSettings } from './appSettings';
import { db } from './firebase';
import { doc, getDoc } from 'firebase/firestore';
import type { Lead, LeadComment, EnhancedDeal } from '../types';

// ==========================================
// SYSTEM PROMPT
// ==========================================

export const SALES_ADVISOR_SYSTEM_PROMPT = `You are an expert sales advisor for a custom timber frame construction business.
The business builds high-end timber frame homes, barns, and commercial structures.
Average project value is $80,000–$400,000. Sales cycles are long (3–12 months).

Your job is to:
- Analyze lead/contact data and recommend the single best next action
- Draft personalized, warm (not salesy) follow-up messages
- Flag leads that are going cold or need urgent attention
- Spot upsell or referral opportunities with past clients

Always be specific. Use the client's name, project type, and history.
Keep follow-up drafts concise — 3–5 sentences max unless asked for more.
Tone: professional, craft-focused, genuine. Never pushy.`;

// ==========================================
// ADVISOR CONTEXT OPTIONS
// ==========================================

export interface AdvisorContext {
    id: string;
    label: string;
    prompt: string;
}

export const ADVISOR_CONTEXTS: AdvisorContext[] = [
    {
        id: 'next-action',
        label: 'Next Action',
        prompt: 'Advise on the single best next action for this lead',
    },
    {
        id: 'draft-followup',
        label: 'Draft Follow-up',
        prompt: 'Draft a follow-up email or text message for this contact',
    },
    {
        id: 'quality-check',
        label: 'Lead Quality Check',
        prompt: 'Assess if this lead is worth pursuing further and explain why',
    },
    {
        id: 'upsell-referral',
        label: 'Upsell / Referral',
        prompt: 'Look for upsell or referral opportunities with this contact',
    },
];

// ==========================================
// PROVIDER / MODEL OPTIONS
// ==========================================

export interface ProviderOption {
    provider: AIProvider;
    model: string;
    label: string;
}

/** All models the advisor can use */
export const AVAILABLE_MODELS: ProviderOption[] = [
    { provider: 'gemini', model: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash' },
    { provider: 'gemini', model: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
    { provider: 'anthropic', model: 'claude-sonnet-4-20250514', label: 'Claude Sonnet 4' },
    { provider: 'anthropic', model: 'claude-3-5-sonnet-20241022', label: 'Claude 3.5 Sonnet' },
    { provider: 'openai', model: 'gpt-4o', label: 'GPT-4o' },
    { provider: 'openai', model: 'gpt-4o-mini', label: 'GPT-4o Mini' },
];

/**
 * Resolve an API key for a provider by checking (in order):
 * 1. appSettings/app_settings (main settings doc)
 * 2. appSettings/aiModels (where Firestore console users often store it)
 * 3. REACT_APP_*_API_KEY environment variable
 */
async function resolveApiKey(provider: AIProvider): Promise<string> {
    // 1. Check main settings doc
    const settings = await getSettings();
    const fromSettings: Record<AIProvider, string | undefined> = {
        gemini: settings.geminiApiKey,
        anthropic: settings.anthropicApiKey,
        openai: settings.openaiApiKey,
    };
    if (fromSettings[provider]) return fromSettings[provider]!;

    // 2. Check appSettings/aiModels doc
    try {
        const aiModelsSnap = await getDoc(doc(db, 'appSettings', 'aiModels'));
        if (aiModelsSnap.exists()) {
            const data = aiModelsSnap.data();
            const fieldMap: Record<AIProvider, string> = {
                gemini: 'geminiApiKey',
                anthropic: 'anthropicApiKey',
                openai: 'openaiApiKey',
            };
            const val = data[fieldMap[provider]];
            if (val) return val;
        }
    } catch (e) {
        // Silently fall through to env check
    }

    // 3. Check environment variable
    const envMap: Record<AIProvider, string> = {
        gemini: 'REACT_APP_GEMINI_API_KEY',
        anthropic: 'REACT_APP_ANTHROPIC_API_KEY',
        openai: 'REACT_APP_OPENAI_API_KEY',
    };
    return process.env[envMap[provider]] || '';
}

/**
 * Get models that have API keys configured.
 * Returns only models whose provider has a key available.
 */
export async function getAvailableModels(): Promise<ProviderOption[]> {
    // Check all providers in parallel
    const [geminiKey, anthropicKey, openaiKey] = await Promise.all([
        resolveApiKey('gemini'),
        resolveApiKey('anthropic'),
        resolveApiKey('openai'),
    ]);

    const keyMap: Record<AIProvider, string> = {
        gemini: geminiKey,
        anthropic: anthropicKey,
        openai: openaiKey,
    };

    return AVAILABLE_MODELS.filter(m => !!keyMap[m.provider]);
}

/**
 * Get the API key for a given provider.
 */
async function getApiKeyForProvider(provider: AIProvider): Promise<string> {
    return resolveApiKey(provider);
}

// ==========================================
// PROMPT BUILDERS
// ==========================================

function daysSince(date: Date | string | undefined | null): string {
    if (!date) return 'Unknown';
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return 'Unknown';
    return String(Math.floor((Date.now() - d.getTime()) / 86400000));
}

/**
 * Build a detailed prompt from lead, deals, and comments for the advisor.
 */
export function buildLeadPrompt(
    lead: Lead,
    deals: EnhancedDeal[],
    comments: LeadComment[],
    context: string,
): string {
    const latestComment = comments.length > 0 ? comments[0] : null;
    const activeDeal = deals.length > 0 ? deals[0] : null;

    // Build location string
    const location = lead.address
        ? `${lead.address.city || ''}, ${lead.address.state || ''}`.replace(/^, |, $/g, '')
        : lead.location || 'Unknown';

    // Build notes section from recent comments
    const recentNotes = comments.slice(0, 5).map(c =>
        `  - [${daysSince(c.createdAt)}d ago] ${c.userName}: ${c.content.substring(0, 200)}`
    ).join('\n') || '  None';

    // Enrichment summary if available
    let enrichmentInfo = '';
    if (lead.enrichment?.score) {
        enrichmentInfo = `\nBUYER SCORE: ${lead.enrichment.score.total}/100 (${lead.enrichment.score.band})`;
    }
    if (lead.enrichment?.person?.jobTitle) {
        enrichmentInfo += `\nOCCUPATION: ${lead.enrichment.person.jobTitle}${lead.enrichment.person.jobCompany ? ` at ${lead.enrichment.person.jobCompany}` : ''}`;
    }

    return `
CONTACT: ${lead.firstName} ${lead.lastName}
EMAIL: ${lead.email || 'Not provided'}
PHONE: ${lead.phone || 'Not provided'}
PROJECT TYPE: ${lead.projectType || 'Unknown'}
LOCATION: ${location}
SOURCE: ${lead.source || 'Unknown'}
STATUS: ${lead.status || 'Unknown'}
LABELS: ${lead.labels?.join(', ') || 'None'}
LEAD CREATED: ${daysSince(lead.createdAt)} days ago${enrichmentInfo}

DEAL INFO:
  Stage: ${activeDeal?.stageId || 'No active deal'}
  Value: ${activeDeal?.value ? '$' + activeDeal.value.toLocaleString() : 'Unknown'}
  Pipeline: ${activeDeal?.pipelineId || 'N/A'}
  Outcome: ${activeDeal?.outcome || 'N/A'}

LAST CONTACT: ${daysSince(latestComment?.createdAt)} days ago
RECENT NOTES:
${recentNotes}

CONTEXT: ${context}

Based on this information, please:
1. Recommend the best next action (1–2 sentences)
2. Draft a follow-up message I can send today
3. Flag any risks or opportunities you notice
  `.trim();
}

/**
 * Build a pipeline review prompt from all open deals.
 */
export function buildPipelinePrompt(deals: EnhancedDeal[]): string {
    const summary = deals.map(d => ({
        name: d.contactName,
        stage: d.stageId,
        value: d.value,
        daysSinceLastStageChange: daysSince(d.currentStageEnteredAt),
        outcome: d.outcome,
        pipeline: d.pipelineId,
    }));

    return `Here is my full open pipeline:\n\n${JSON.stringify(summary, null, 2)}\n\nPlease:
1. Rank the top 3 leads I should contact this week and why
2. Flag any leads at risk of going cold (no stage change in 30+ days)
3. Estimate which deals are most likely to close in the next 60 days
4. Suggest one action to improve overall pipeline health`;
}

// ==========================================
// ADVISOR FUNCTIONS
// ==========================================

/**
 * Ask the AI advisor about a specific lead.
 */
export async function askAdvisor(
    providerOption: ProviderOption,
    lead: Lead,
    deals: EnhancedDeal[],
    comments: LeadComment[],
    context: string,
): Promise<AIResponse> {
    const apiKey = await getApiKeyForProvider(providerOption.provider);
    if (!apiKey) {
        throw new Error(`No API key configured for ${providerOption.provider}. Add one in Settings.`);
    }

    const config: AIConfig = {
        provider: providerOption.provider,
        apiKey,
        model: providerOption.model,
    };

    const prompt = buildLeadPrompt(lead, deals, comments, context);

    return callAI(
        config,
        [{ role: 'user', content: prompt }],
        SALES_ADVISOR_SYSTEM_PROMPT,
    );
}

/**
 * Ask the AI for a pipeline review across all open deals.
 */
export async function askPipelineReview(
    providerOption: ProviderOption,
    deals: EnhancedDeal[],
): Promise<AIResponse> {
    const apiKey = await getApiKeyForProvider(providerOption.provider);
    if (!apiKey) {
        throw new Error(`No API key configured for ${providerOption.provider}. Add one in Settings.`);
    }

    const config: AIConfig = {
        provider: providerOption.provider,
        apiKey,
        model: providerOption.model,
    };

    const prompt = buildPipelinePrompt(deals);

    return callAI(
        config,
        [{ role: 'user', content: prompt }],
        SALES_ADVISOR_SYSTEM_PROMPT,
    );
}
